import { DotLegalDeleteDialog, DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import React, { useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useContractResponsibilityHelper } from "../ContractResponsibilityHelper.hooks.ts";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery.ts";
import { ContractModel } from "../Contract.types.ts";
import { Trans } from "react-i18next";
import RemoveFromExcludedListDialog from "../../contractOverview/contractAccessTab/removeFromExcludedListDialog/RemoveFromExcludedListDialog.tsx";
import { UserSelectableItem } from "../../../user/User.types.ts";

export interface ExcludedUsersFieldProps {
    /** Lets a caller that owns the vertical rhythm itself turn the field's own margin off. */
    noMargin?: boolean;
    contractId: string | undefined;
    isLoading: boolean;
    contract: ContractModel | undefined;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    readOnly: boolean;
    required: boolean;
    isDisabled: boolean;
    isPreview: boolean;
    errorText?: string;
    disableAccessWarning?: boolean;
    showWarningAsInfo?: boolean;
    /** Suppresses the field's help icon, for a view where the same field is shown twice. */
    hideToolTip?: boolean;
}

function ExcludedUsersField(props: ExcludedUsersFieldProps) {
    const [removeUserFromExcludedListChanges, setRemoveUserFromExcludedListChanges] = useState<ContractModel | null>(null);
    const [userToRemoveFromExcludedList, setUserToRemoveFromExcludedList] = useState<UserSelectableItem | null>(null);

    const { translateString } = useTranslation();
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useContractResponsibilityHelper(
        props.onChange,
        props.disableAccessWarning
    );

    const userQuery = useResponsibleQuery(!props.isPreview);

    async function onConfirmRemoveUserFromExcludedList() {
        await props.onChange((contract: ContractModel) => {
            Object.assign(contract, removeUserFromExcludedListChanges!);
        });

        setRemoveUserFromExcludedListChanges(null);
        setUserToRemoveFromExcludedList(null);
    }

    return (
        <>
            <DotLegalMultiSelect
                noMargin={props.noMargin}
                options={
                    props.contract === undefined
                        ? []
                        : (userQuery.dataByList(props.contract.excludedUsers ?? []) ?? [])
                              .filter((x) => x.id !== props.contract!.contractManagerId && !props.contract?.additionalAccessUsers.includes(x.id))
                              .map((x) => ({ ...x, name: `${x.name}${x.active ? "" : ` (${translateString("deactivated")})`}` }))
                }
                isLoading={(userQuery.isLoading || props.isLoading) && !props.readOnly}
                onChange={() => {}}
                label={translateString("excludedUsers")}
                noOptionsLabel={translateString("noOptions")}
                warningText={props.isPreview || props.showWarningAsInfo ? undefined : translateString("contractExcludedUsersTooltip")}
                toolTipText={
                    props.hideToolTip
                        ? undefined
                        : props.showWarningAsInfo
                          ? `${translateString("contractExcludedAccessTooltip")}\n\n${translateString("contractExcludedUsersTooltip")}`
                          : translateString("contractExcludedAccessTooltip")
                }
                disableClearable={props.required}
                chosenOptions={props.contract?.excludedUsers ?? []}
                disabled={props.isDisabled}
                errorText={props.errorText}
                onItemDeleted={
                    props.readOnly
                        ? () => {}
                        : (removedUser) => {
                              let users = props.contract!.excludedUsers.filter((excludedUser) => excludedUser !== removedUser);
                              let newContract = { ...props.contract!, excludedUsers: users };

                              setRemoveUserFromExcludedListChanges(newContract);
                              const userBeingRemoved = userQuery.userData?.find((x) => x.id === removedUser);
                              if (userBeingRemoved) {
                                  setUserToRemoveFromExcludedList(userBeingRemoved);
                              }
                          }
                }
                onItemAdded={
                    props.readOnly
                        ? () => {}
                        : (addedUser) => {
                              let newContract = { ...props.contract!, excludedUsers: [...props.contract!.excludedUsers, addedUser] };
                              onFieldChange(newContract, props.contract!);
                          }
                }
            />
            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmContractAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}

            {removeUserFromExcludedListChanges && userToRemoveFromExcludedList && (
                <RemoveFromExcludedListDialog
                    contractId={props.contractId!}
                    userId={userToRemoveFromExcludedList.id}
                    userFullName={userToRemoveFromExcludedList.name}
                    onCloseDialog={() => {
                        setRemoveUserFromExcludedListChanges(null);
                        setUserToRemoveFromExcludedList(null);
                    }}
                    onRemoveFromExcludedList={() => onConfirmRemoveUserFromExcludedList()}
                />
            )}
        </>
    );
}

export default ExcludedUsersField;
